// hooks/useInfiniteSelectOptions.js
import { useState, useRef, useCallback, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

const DEBOUNCE_DELAY = 500;

/**
 * Custom hook to manage data fetching for Ant Design Select with infinite scroll and search.
 *
 * @param {string} queryKeyBase - The base key for React Query (e.g., 'categories', 'authors').
 * @param {function} apiFn - The API function to call (e.g., getCategories, getAuthors).
 * It should accept { page, limit, searchTerm } and return { data: [], hasMore: boolean }.
 * @param {string} [idField='id'] - The field name for the option's value.
 * @param {string} [labelField='name'] - The field name for the option's label.
 * @returns {object} Contains options, loading states, and handlers for Ant Design Select.
 */
const useInfiniteSelectOptions = (queryKeyBase: any, apiFn: any, idField = 'id', labelField = 'name') => {
  const [searchTerm, setSearchTerm] = useState('');
  const fetchTimeoutRef = useRef<any>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: [queryKeyBase, searchTerm],
    queryFn: ({ pageParam }) => apiFn({ page: pageParam, limit: 10, searchTerm }, true), // Removed default here
    getNextPageParam: ({ current_page, last_page }) => {
      return current_page < last_page ? current_page + 1 : undefined;
    },
    initialPageParam: 1,
    placeholderData: (previousData) => previousData,
  });

  const options = data?.pages.flatMap((page: any) =>
    page.data.map((item: any) => ({
      value: item[idField],
      label: item[labelField],
    }))
  ) || [];

  const handleSearch = useCallback((value: any) => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    fetchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
    }, DEBOUNCE_DELAY);
  }, []);

  const handlePopupScroll = useCallback((e: any) => {
    const { target } = e;
    if (
      !isFetchingNextPage &&
      hasNextPage &&
      target.scrollTop + target.offsetHeight === target.scrollHeight
    ) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  useEffect(() => {
    if (isError) {
      console.error(`Error fetching ${queryKeyBase} options:`, error);
    }
  }, [isError, error, queryKeyBase]);

  return {
    options,
    isLoading: isLoading || isFetchingNextPage,
    handleSearch,
    handlePopupScroll,
    isError,
    error,
  };
};

export default useInfiniteSelectOptions;